import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import PasswordInput from "@galaxy-io/dls/inputs/PasswordInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Inline, Stack } from "@/components/workspace/people-styles";
import { SourceIcon } from "@/components/workspace/source-icon";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { ConnectionSurface } from "./connection-surface";
import { IntegrationDrawerContent } from "./integration-drawer-content";
import { useConnectionReset } from "./use-connection-reset";

export function AnthropicConnection({
  open,
  selected,
  onClose,
  onSaved,
}: {
  open: boolean;
  selected?: Integration;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const router = useRouter(),
    { toast } = useToast();
  const [name, setName] = useState(selected?.name || "Anthropic"),
    [apiKey, setApiKey] = useState(""),
    [busy, setBusy] = useState(false);
  function notify(header: string, subheader: string) {
    toast({
      variant: ToastVariant.SUCCESS,
      header,
      description: subheader,
      ...toastDefaults(ToastVariant.SUCCESS),
    });
  }
  useConnectionReset(open, !!selected, () => {
    setFormError(undefined);
    setName(selected?.name || "Anthropic");
    setApiKey("");
    setBusy(false);
  });
  async function action<T>(request: () => Promise<T>) {
    setFormError(undefined);
    setBusy(true);
    try {
      const data = await request();
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      router.refresh();
      return data;
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <ConnectionSurface
      open={open}
      selected={selected}
      flush
      title={selected?.name || "Connect Anthropic"}
      headerLeading={<SourceIcon source="anthropic" size={36} />}
      onClose={onClose}
      busy={busy}
      dirty={!!apiKey || name !== (selected?.name || "Anthropic")}
      footer={
        <Button
          label={selected ? "Save connection" : "Connect Anthropic"}
          isLoading={busy}
          isDisabled={busy || !name.trim() || (!selected && !apiKey)}
          onClick={async () => {
            const body = { name, apiKey: apiKey || undefined };
            const result = await action(() =>
              selected
                ? unwrap(
                    apiClient().PUT("/integrations/anthropic/connections/{id}", {
                      params: { path: { id: selected.id } },
                      body,
                    }),
                  )
                : unwrap(
                    apiClient().POST("/integrations/anthropic/connections", {
                      body,
                    }),
                  ),
            );
            if (result) {
              setApiKey("");
              notify("Anthropic configured", "Outbound can draft outreach.");
              onSaved(result.id);
            }
          }}
        />
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <IntegrationDrawerContent>
        <Stack>
          <TextInput
            ref={(input) => input?.setAttribute("aria-label", "Integration name")}
            label="Integration name"
            value={name}
            onChange={setName}
            variant={InputVariant.BASE}
            fillWidth
            isDisabled={busy}
          />
          <PasswordInput
            ref={(input) => input?.setAttribute("aria-label", "API key")}
            label="API key"
            placeholder={selected ? "Leave blank to keep the saved key" : "sk-ant-…"}
            value={apiKey}
            onChange={setApiKey}
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          {selected?.enabled && (
            <Inline>
              <Button
                label="Test connection"
                variant={ButtonVariant.SECONDARY}
                isDisabled={busy}
                onClick={async () => {
                  const result = await action(() =>
                    unwrap(
                      apiClient().POST("/integrations/anthropic/connections/{id}/test", {
                        params: { path: { id: selected.id } },
                      }),
                    ),
                  );
                  if (result) notify("API key valid", `${result.model} is available.`);
                }}
              />
            </Inline>
          )}
        </Stack>
      </IntegrationDrawerContent>
    </ConnectionSurface>
  );
}
